import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { X } from "lucide-react";
import type { ComponentProps } from "react";
import { cn } from "@/lib/utils";

/**
 * The centered modal used by the alerts, manual, decision, connect-manual,
 * journal-detail and project-documents overlays in `Visual - Demo.dc.html`.
 *
 * Every one of them is the same shell: a blurred ink scrim at z-60 with 40px of
 * padding, and a white 16px-radius panel capped at 86vh that scrolls its body
 * between a fixed header and an optional footer. The panel is nested *inside*
 * the overlay because the design centres it with flexbox on the scrim itself.
 *
 * The z-index ladder in the source is 60 (standard) < 65 (modify decision) <
 * 70 (scan flow); pass `overlayClassName="z-65"` for a stacked modal.
 *
 * Above all of them sits the select popover at 80 (`ui/select.tsx`). It is not
 * a step on this ladder — a popover has to clear whatever surface opened it, and
 * when it did not, a select inside a drawer opened silently behind the panel.
 * Anything else portalled out of a modal needs to clear 70 the same way.
 */
const dialogPanelVariants = cva(
  [
    "relative flex max-h-[86vh] max-w-full flex-col overflow-hidden",
    "rounded-panel bg-surface shadow-modal animate-rise",
  ],
  {
    variants: {
      /**
       * The widths that actually occur in the design. 480px and 620px also
       * appear once each — those callers pass `className="w-[480px]"`.
       */
      size: {
        sm: "w-[440px]",
        md: "w-[460px]",
        lg: "w-[520px]",
        xl: "w-[600px]",
        "2xl": "w-[640px]",
        "3xl": "w-[960px]",
      },
    },
    defaultVariants: { size: "md" },
  },
);

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogPortal = DialogPrimitive.Portal;
export const DialogClose = DialogPrimitive.Close;

type DialogOverlayProps = ComponentProps<typeof DialogPrimitive.Overlay>;

export function DialogOverlay({ className, ...props }: DialogOverlayProps) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "fixed inset-0 z-60 flex items-center justify-center p-10",
        "bg-ink/50 backdrop-blur-[3px]",
        className,
      )}
      {...props}
    />
  );
}

type DialogContentProps = ComponentProps<typeof DialogPrimitive.Content> &
  VariantProps<typeof dialogPanelVariants> & {
    /** Extra classes for the scrim — most often a higher `z-*` for stacking. */
    overlayClassName?: string;
  };

export function DialogContent({
  className,
  overlayClassName,
  size,
  children,
  ...props
}: DialogContentProps) {
  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName}>
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(dialogPanelVariants({ size }), className)}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogOverlay>
    </DialogPortal>
  );
}

/**
 * `padding:18px 22px` with a `border-inner` rule underneath. A handful of
 * modals in the source use `18px 20px` instead; those pass `px-5`.
 */
export function DialogHeader({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "flex shrink-0 items-center justify-between gap-3",
        "border-b-[1.5px] border-border-inner px-[22px] py-[18px]",
        className,
      )}
      {...props}
    />
  );
}

export function DialogTitle({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        // Deliberately NOT Oswald: modal titles are the one heading in the
        // design that stays Inter and sentence case (`font-weight:700;
        // font-size:1.05rem`, no family, no transform).
        "min-w-0 text-[1.05rem] font-bold text-ink",
        className,
      )}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-[0.86rem] leading-[1.55] text-muted", className)}
      {...props}
    />
  );
}

/** The 36px round close button. Identical in every modal and both drawers. */
export function DialogCloseButton({
  className,
  "aria-label": ariaLabel = "Close",
  ...props
}: ComponentProps<typeof DialogPrimitive.Close>) {
  return (
    <DialogPrimitive.Close
      data-slot="dialog-close"
      aria-label={ariaLabel}
      className={cn(
        "flex size-9 shrink-0 cursor-pointer items-center justify-center",
        "rounded-full border-[1.5px] border-border bg-subtle text-ink",
        "transition-colors hover:border-border-hover",
        className,
      )}
      {...props}
    >
      <X size={18} />
    </DialogPrimitive.Close>
  );
}

/** The scrolling middle band. Alerts-style modals narrow this to `px-5`. */
export function DialogBody({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-body"
      className={cn("flex-1 overflow-y-auto p-[22px]", className)}
      {...props}
    />
  );
}

export function DialogFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex shrink-0 items-center justify-end gap-2.5",
        "border-t-[1.5px] border-border-inner bg-surface-alt px-[22px] py-4",
        className,
      )}
      {...props}
    />
  );
}

export { dialogPanelVariants };
export type { DialogContentProps };
